"use client";
import { Avatar, Box, Typography } from "@mui/material";
import { Favorite } from "@mui/icons-material";
import React from "react";
import { useCurrentUser } from "../../hooks/useCurrentUser";

interface ThankYouSlideProps {
  userName: string;
  isClient: boolean;
  forSharing: boolean;
}

export default function ThankYouSlide({
  userName,
  isClient,
  forSharing,
}: ThankYouSlideProps) {
  const { photoUrl: userAvatar } = useCurrentUser();

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#0a0a0a",
        position: "relative",
        overflow: "hidden",
        color: "white",
        p: 4,
        textAlign: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "150%",
          height: "150%",
          background:
            "radial-gradient(circle, rgba(236, 72, 153, 0.3) 0%, rgba(6, 182, 212, 0.2) 40%, transparent 70%)",
          animation: "pulse 3s ease-in-out infinite",
        },
        "@keyframes pulse": {
          "0%, 100%": {
            transform: "translate(-50%, -50%) scale(1)",
            opacity: 0.8,
          },
          "50%": {
            transform: "translate(-50%, -50%) scale(1.1)",
            opacity: 1,
          },
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Favorite
            sx={{
              fontSize: 100,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              animation: "heartbeat 1.5s ease-in-out infinite",
              "@keyframes heartbeat": {
                "0%, 100%": {
                  transform: "scale(1)",
                },
                "10%": {
                  transform: "scale(1.1)",
                },
                "20%": {
                  transform: "scale(1)",
                },
                "30%": {
                  transform: "scale(1.1)",
                },
                "40%": {
                  transform: "scale(1)",
                },
              },
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 3,
              animation: "fadeInUp 0.6s ease-out 0.2s both",
              "@keyframes fadeInUp": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(20px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            {`Our Favorite Stridist ${isClient ? "Athlete" : "Coach"}`}
          </Typography>
          {userAvatar && (
            <Avatar
              src={userAvatar}
              alt={userName}
              sx={{
                width: 160,
                height: 160,
                border: "4px solid transparent",
                backgroundImage:
                  "linear-gradient(#0a0a0a, #0a0a0a), linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)",
                backgroundOrigin: "border-box",
                backgroundClip: "padding-box, border-box",
                animation: "fadeInScale 0.8s ease-out 0.6s both",
                boxShadow:
                  "0 10px 40px rgba(236, 72, 153, 0.3), 0 0 60px rgba(6, 182, 212, 0.2)",
                "@keyframes fadeInScale": {
                  "0%": {
                    opacity: 0,
                    transform: "scale(0.8)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "scale(1)",
                  },
                },
              }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mb: 4,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                background: !forSharing
                  ? "linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)"
                  : undefined,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: "fadeInUp 0.6s ease-out 0.4s both",
                "@keyframes fadeInUp": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(20px)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              It's you, {userName}!
            </Typography>
          </Box>
        </Box>

        {!forSharing && (
          <>
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                opacity: 0.9,
                animation: "fadeInUp 0.6s ease-out 0.6s both",
                "@keyframes fadeInUp": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(20px)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              2025 was just the beginning!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: 600,
                mb: 2,
                opacity: 0.9,
                animation: "fadeInUp 0.6s ease-out 0.6s both",
                "@keyframes fadeInUp": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(20px)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              {isClient
                ? "You trusted your coach and took bold steps toward your goals. We are honored to be part of your journey."
                : "You built a community, broke records, and changed lives. We are so proud to be part of your journey."}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                opacity: 0.8,
                animation: "fadeInUp 0.6s ease-out 0.8s both",
                "@keyframes fadeInUp": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(20px)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              The best is yet to come. See you in 2026!
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
